import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { ImageCropperDefaultConfiguration, MessageQueryService, SmartModalService } from '@certemy/ui-components';
import { EntityForImageUpdate } from 'components/upload-image-modal';
import { ComputedFrom } from '@certemy/common';
import { Actions } from 'features/profiles-state/+state/profiles-state.actions';
import { UploadImageModalService } from 'components/upload-image-modal/upload-image-modal.service';
import { BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ResizeService } from 'services/resize/resize.service';
import { Store } from '@ngrx/store';
import { OrgUserProfile } from 'interfaces/profile.interface';
import { PROFILE_TYPE_ID } from 'components/organization-users/users/users.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IImageConfig } from 'components/profile-card/profile-card.constant';
import { UserProfile } from 'services/user/user.interface';

@UntilDestroy()
@Component({
  selector: 'cm-profile-card-avatar',
  templateUrl: './profile-card-avatar.component.html',
  styleUrls: ['./profile-card-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileCardAvatarComponent implements OnDestroy {
  @Input() profile: OrgUserProfile;
  @Input() canUpdateAvatar = false;
  @Input() currentProfile: UserProfile;
  @Input() manualUpdate = false;
  @Input() userId: number;
  @Output() updateAvatar = new EventEmitter<any>();

  imageLoading$ = new BehaviorSubject(false);
  isEditAvailable$ = this.resizeService.resize$.pipe(map(({ isDesktop }) => isDesktop));

  constructor(
    private uploadImageModalService: UploadImageModalService,
    private resizeService: ResizeService,
    private changeDetector: ChangeDetectorRef,
    private store: Store<any>,
    private messageQueryService: MessageQueryService,
    private smartModalService: SmartModalService,
  ) {}

  ngOnDestroy() {}

  onEditLogoClick() {
    this.openUploadModal({
      configuration: this.profile.avatar.imageConfiguration,
      originalFile: this.profile.avatar.originalImageFile,
    });
  }

  openUploadModal(defaultConfiguration: ImageCropperDefaultConfiguration = null) {
    if (!this.canUpdateAvatar) {
      return;
    }

    const modalConfig = {
      defaultConfiguration,
      entityConfig: {
        type: EntityForImageUpdate.USER,
        id: this.profile.userId,
      },
      resetScreenMessage: `Avatar will be reset to system default. Changes will be applied to all your profiles. Proceed with changes?`,
      manualUpdate: this.manualUpdate,
    };

    if (this.profile.hasProfilesInOtherOrganizations && this.profile.profile_id !== this.currentProfile.profile_id) {
      this.smartModalService.openNotification({
        title: 'WARNING',
        body: 'The profile you are trying to edit is associated with multiple organizations. Please contact support@certemy.com to edit this profile.',
        closeButtonText: 'Ok',
      }).pipe(take(1)).subscribe();

      return;
    }

    this.uploadImageModalService
      .openUploadModal(modalConfig, this.imageLoading$)
      .pipe(untilDestroyed(this))
      .subscribe(([imageConfig]: IImageConfig[]) =>
        this.emitUpdateAvatar(imageConfig));
  }

  private emitUpdateAvatar(imageConfig: IImageConfig) {
    this.imageLoading$.next(false);
    this.profile = {
      ...this.profile,
      avatar: imageConfig,
      image: imageConfig.imageFile,
    };
    this.updateAvatar.emit({
      profile: this.profile,
      imageConfig,
    });
    this.changeDetector.markForCheck();
    if (!this.manualUpdate && this.profile.profile_id === this.currentProfile.profile_id) {
      this.store.dispatch(new Actions.GetProfiles());
      this.messageQueryService.addMessage('Uploaded avatar is applied to all profiles within Certemy');
    }
  }

  @ComputedFrom('profile')
  get profileAvatarPlaceholder() {
    return this.profile.profile_type_id === PROFILE_TYPE_ID.FACILITY
      ? '/assets/images/default-company.svg'
      : '/assets/images/professional/profile-avatar/placeholder.png';
  }
}
